/**
 * Goods Receipt Form Modal
 *
 * Modal to create a new goods receipt.
 * Two modes:
 * - From order (orderId provided) - lines pre-filled from order
 * - Free receipt (no orderId) - manual supplier selection and product addition
 *
 * @see US-9.5 - Formulaire Reception
 */

import { AlertTriangle, CheckCircle } from 'lucide-react';
import {
  Button,
  Modal,
  ModalFooter,
  Skeleton,
} from '@/components/ui';
import { useCurrency } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useGoodsReceiptForm } from '../hooks/useGoodsReceiptForm';
import {
  ReceiptFormHeader,
  ReceiptInfoSection,
  ReceiptLinesTable,
  ReceiptNotesSection,
} from './receipt-form';

interface GoodsReceiptFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId?: string | null;
  onSuccess?: () => void;
}

export function GoodsReceiptFormModal({
  isOpen,
  onClose,
  orderId,
  onSuccess,
}: GoodsReceiptFormModalProps) {
  const currency = useCurrency();

  const form = useGoodsReceiptForm({
    isOpen,
    orderId,
    onClose,
    onSuccess,
  });

  // Loading state
  if (form.isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={form.handleClose} size="5xl">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </Modal>
    );
  }

  // Error state
  if (form.isError) {
    return (
      <Modal isOpen={isOpen} onClose={form.handleClose} title="Erreur" size="md">
        <div className="text-center py-6">
          <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-dark-500 mb-2">
            Erreur de chargement
          </h2>
          <p className="text-neutral-600 mb-4">
            Impossible de charger la commande. Elle a peut-etre deja ete entierement recue.
          </p>
        </div>
        <ModalFooter>
          <Button onClick={form.handleClose}>Fermer</Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={form.handleClose} size="5xl">
      {/* Header */}
      <ReceiptFormHeader
        isFromOrder={form.isFromOrder}
        orderNumber={form.orderNumber}
        supplierName={form.supplierName}
        isFormValid={!!form.isFormValid}
        isSaving={form.isSaving}
        isValidating={form.isValidating}
        onClose={form.handleClose}
        onSaveDraft={form.handleSaveDraft}
        onValidate={form.handleValidate}
      />

      {/* Content - scrollable area */}
      <div className="max-h-[calc(100vh-12rem)] overflow-auto space-y-6">
        {/* Error messages */}
        {form.errors.length > 0 && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-danger-800">
                  Correction requise
                </h3>
                <ul className="mt-2 text-sm text-danger-700 space-y-1">
                  {form.errors.map((error, i) => (
                    <li key={i}>{error.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Section: Informations */}
        <ReceiptInfoSection
          isFromOrder={form.isFromOrder}
          supplierId={form.supplierId}
          receiptDateRange={form.receiptDateRange}
          supplierInvoiceRef={form.supplierInvoiceRef}
          blNumber={form.blNumber}
          isDefinitive={form.isDefinitive}
          supplierOptions={form.supplierOptions}
          suppliersLoading={form.suppliersLoading}
          onSupplierChange={form.handleSupplierChange}
          onDateChange={form.handleDateChange}
          onInvoiceRefChange={form.handleInvoiceRefChange}
          onBlNumberChange={form.handleBlNumberChange}
          onIsDefinitiveChange={form.handleIsDefinitiveChange}
          onBlFileSelect={form.handleBlFileSelect}
          onInvoiceFileSelect={form.handleInvoiceFileSelect}
          pendingBlFiles={form.pendingBlFiles}
          pendingInvoiceFiles={form.pendingInvoiceFiles}
          onRemovePendingBlFile={form.handleRemovePendingBlFile}
          onRemovePendingInvoiceFile={form.handleRemovePendingInvoiceFile}
        />

        {/* Section: Lines */}
        <ReceiptLinesTable
          items={form.items}
          isFromOrder={form.isFromOrder}
          currency={currency}
          totals={form.totals}
          errors={form.errors}
          supplierId={form.supplierId}
          onQuantityChange={form.handleQuantityChange}
          onVarianceReasonChange={form.handleVarianceReasonChange}
          onRemoveItem={form.handleRemoveItem}
          onAddItem={form.handleAddItem}
          onPackagingChange={form.handlePackagingChange}
        />

        {/* Section: Notes */}
        <ReceiptNotesSection
          notes={form.notes}
          onChange={form.handleNotesChange}
        />
      </div>

      {/* Validate confirmation modal */}
      <Modal
        isOpen={form.showValidateConfirm}
        onClose={form.closeValidateConfirm}
        title="Valider la reception ?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-4">
          La validation de cette reception va :
        </p>
        <ul className="text-sm text-neutral-600 mb-4 list-disc ml-4 space-y-1">
          <li>Mettre a jour les quantites recues sur la commande</li>
          <li>Recalculer le Prix Moyen Pondere (PMP) des produits</li>
          <li>Rendre cette reception non modifiable</li>
        </ul>
        <p className="text-sm font-medium text-dark-500 mb-4">
          Total HT : {formatCentimes(form.totals.totalHT, currency)}
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={form.closeValidateConfirm}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={form.confirmValidate}
            disabled={form.isValidating || form.isSaving}
          >
            <CheckCircle className="w-4 h-4" />
            Confirmer la validation
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={form.showCancelConfirm}
        onClose={form.closeCancelConfirm}
        title="Quitter sans sauvegarder ?"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-4">
          Vous avez des modifications non sauvegardees. Etes-vous sur de vouloir quitter ?
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={form.closeCancelConfirm}>
            Continuer l'edition
          </Button>
          <Button variant="danger" onClick={form.confirmCancel}>
            Quitter sans sauvegarder
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
}
